import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PublicMeetingRoomApiService } from '../services/public-meeting-room-api.service';
import { PublicMeetingRoomResponse } from '../models/meeting.model';
import { JitsiRoomComponent } from '../jitsi-room/jitsi-room.component';

/**
 * Página pública (sin login) de una sala de videollamada Jitsi.
 * Ruta: /#/meeting-room/:token (fuera del authGuard).
 *
 * Flujo: lee :token → GET /api/public/meeting-room/:token → muestra la portada
 * con un nombre opcional → al entrar monta <app-jitsi-room>.
 */
@Component({
  selector: 'app-public-meeting-room',
  standalone: true,
  imports: [CommonModule, FormsModule, JitsiRoomComponent],
  templateUrl: './public-meeting-room.component.html',
})
export class PublicMeetingRoomComponent implements OnInit {
  token = '';
  loading = true;
  error = '';
  notFound = false;

  room: PublicMeetingRoomResponse | null = null;

  // Portada previa: el invitado puede escribir su nombre antes de entrar.
  guestName = '';
  joined = false;

  constructor(
    private route: ActivatedRoute,
    private publicApi: PublicMeetingRoomApiService
  ) {}

  ngOnInit(): void {
    this.token =
      this.route.snapshot.paramMap.get('token') ||
      this.route.snapshot.queryParamMap.get('token') ||
      '';

    if (!this.token) {
      this.error = 'Enlace de reunión inválido.';
      this.notFound = true;
      this.loading = false;
      return;
    }

    this.publicApi.getRoom(this.token).subscribe({
      next: (room) => {
        this.room = room;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 404) {
          this.notFound = true;
          this.error = 'Sala no encontrada.';
        } else {
          this.error = err?.error?.message || 'No se pudo cargar la sala de reunión.';
        }
      },
    });
  }

  join(): void {
    if (!this.room) return;
    this.joined = true;
  }

  onClose(): void {
    this.joined = false;
  }

  formatFecha(): string {
    if (!this.room?.datetimeStart) return '';
    try {
      const fecha = new Intl.DateTimeFormat('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(this.room.datetimeStart));
      return this.room.durationMinutes
        ? `${fecha} · ${this.room.durationMinutes} min`
        : fecha;
    } catch {
      return '';
    }
  }
}
